'use client'

import { useEffect, useId, useState } from 'react'

import { IconStar } from '@/app/components/atoms/Icons'
import { cx, isRatingWidgetDisabled } from '@/app/lib/utils'
import { isVoteEligible } from '@/app/lib/ratings'
import { submitVote } from '@/app/lib/ratings/actions'

import { useVotedPerformances } from './hooks/useVotedPerformances'
import styles from './OwnRatingForm.module.scss'
import type { OwnRatingFormProps } from './OwnRatingForm.types'

const STARS = [1, 2, 3, 4, 5] as const
const ERROR_MESSAGE =
  'Ups, no se ha podido completar el proceso. Por favor inténtalo de nuevo más tarde.'

type Status = 'idle' | 'submitting' | 'error'

/**
 * `OwnRatingForm` — formulario de voto del sistema propio (BD), fiel al
 * proyecto de diseño (Figma "Valoración del evento" / "Valoraciones del
 * evento (N)" / "valoraciones deshabilitadas", specs/046-valoraciones-bazaarvoice-bd):
 * estrellas seleccionables mediante inputs de radio nativos (navegación por
 * teclado y semántica de grupo de opción única gratis), sin ningún enlace
 * para dejar comentario (FR-013 — el diseño de referencia sí incluye
 * "Dejanos tu opinión", pero para BD se omite a propósito).
 *
 * Tres estados mutuamente excluyentes:
 * - **Ya votado** (`voted_performances` en `localStorage`): estrellas
 *   deshabilitadas, texto "Ya has votado".
 * - **Cookies rechazadas** (`isRatingWidgetDisabled()`): estrellas
 *   deshabilitadas, texto "No puedes valorar en este momento" (Figma, node
 *   5900:35039) — el voto en sí no depende de esto (FR-012), pero mostrarlo
 *   como no disponible evita que el usuario espere que su voto se recuerde.
 * - **Habilitado**: estrellas en acento; un clic vota inmediatamente (no hay
 *   botón de envío separado, ya que no existe "Dejanos tu opinión" en BD).
 *
 * Ambos flags de deshabilitación se resuelven en `useEffect` (nunca en el
 * cuerpo de render): son fuentes browser-only (`localStorage`/cookie), y un
 * Client Component también renderiza en servidor (architecture.rule.md) — el
 * estado por defecto (habilitado) coincide en servidor y primer render de
 * cliente, sin *hydration mismatch*.
 */
export function OwnRatingForm({ performanceId, prompt, variant }: OwnRatingFormProps) {
  const legendId = useId()
  const { votedPerformances, markAsVoted } = useVotedPerformances()
  const [selected, setSelected] = useState<number | null>(null)
  const [status, setStatus] = useState<Status>('idle')
  const [cookiesDisabled, setCookiesDisabled] = useState(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- lectura de cookie post-hidratación, ver architecture.rule.md
    setCookiesDisabled(isRatingWidgetDisabled())
  }, [])

  const alreadyVoted =
    votedPerformances != null && !isVoteEligible(votedPerformances, performanceId)
  const isDisabled = alreadyVoted || cookiesDisabled
  const disabledMessage = alreadyVoted ? 'Ya has votado' : 'No puedes valorar en este momento'

  async function handleSelect(value: number) {
    if (status === 'submitting') return

    setSelected(value)
    setStatus('submitting')

    const result = await submitVote(performanceId, value)
    if (result.ok) {
      markAsVoted(performanceId)
      setStatus('idle')
    } else {
      setStatus('error')
    }
  }

  return (
    <div className={cx(styles.form, variant === 'empty' && styles.formEmpty)}>
      <p className={styles.prompt}>{prompt}</p>

      <fieldset className={styles.fieldset} disabled={isDisabled} aria-busy={status === 'submitting'}>
        <legend id={legendId} className={styles.legend}>
          Puntúa este evento
        </legend>

        <div className={cx(styles.stars, isDisabled && styles.starsDisabled)}>
          {STARS.map((starValue) => (
            <label key={starValue} className={styles.starLabel}>
              <input
                type="radio"
                name={`own-rating-${performanceId}`}
                value={starValue}
                checked={selected === starValue}
                disabled={isDisabled}
                onChange={() => handleSelect(starValue)}
                className={styles.input}
              />
              <span className={styles.srLabel}>
                {starValue} {starValue === 1 ? 'estrella' : 'estrellas'}
              </span>
              <IconStar size={36} className={styles.star} />
            </label>
          ))}
        </div>
      </fieldset>

      {isDisabled && (
        <p role="status" className={styles.disabledMessage}>
          {disabledMessage}
        </p>
      )}

      {status === 'error' && (
        <p role="alert" className={styles.error}>
          {ERROR_MESSAGE}
        </p>
      )}
    </div>
  )
}
